import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import SendIcon from '@mui/icons-material/Send';
import MicIcon from '@mui/icons-material/Mic';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckIcon from '@mui/icons-material/Check';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CloseIcon from '@mui/icons-material/Close';

import AuthContext from '../authAndConnections/auth';
import AxiosGlobal from '../authAndConnections/axiosGlobalUrl';
import { usePermissions } from '../../contextApi/PermissionContext';
import {
  fetchRawContentChat, sendRawContentChatMessage,
  editRawContentChatMessage, deleteRawContentChatMessage, markRawContentChatSeen,
  actions,
} from '../../store/store';
import MediaViewer, { resolveMediaKind } from './mediaViewer';
import { playbackUrl } from '../../tools/videoSource';
import UserAvatar from '../main/userAvatar';
import ConfirmDialog from '../../tools/modal/confirmDialog';

const fmtTime = (d) => {
  const dt = new Date(d);
  return `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
};

// Real-time chat — text, voice, or file messages, Telegram-style. Lives on
// EITHER a raw content record OR a standalone ready-to-upload record (one
// with no source raw content to hang the thread off) — pass exactly one of
// rawContentId / readyToUploadId. One Socket.io room per entity; the
// frontend joins on mount and leaves on unmount.
export default function RawContentChat({ rawContentId, readyToUploadId, T, isDark }) {
  const { t }       = useTranslation();
  const dispatch    = useDispatch();
  const authCtx     = useContext(AuthContext);
  const axiosGlobal = useContext(AxiosGlobal);
  const { can }     = usePermissions();

  const entityId = rawContentId || readyToUploadId;
  const kind     = rawContentId ? 'rawContent' : 'readyToUpload';

  const messages = useSelector(s => s.dmRawContentChat);
  const total    = useSelector(s => s.dmRawContentChatTotal);
  const ownerId  = useSelector(s => s.dmRawContentChatOwnerId);
  const myId     = String(authCtx.decode?.id || authCtx.decode?._id || '');

  const [loading, setLoading]   = useState(true);
  const [body, setBody]         = useState('');
  const [sending, setSending]   = useState(false);
  const [sendProgress, setSendProgress] = useState(null);   // 0-100 while an attachment uploads
  const [recording, setRecording] = useState(false);
  const [recordError, setRecordError] = useState('');
  const [viewerMedia, setViewerMedia] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef        = useRef([]);
  const scrollRef         = useRef(null);

  // Marks everything not-mine as seen — on initial load and again whenever a
  // new message arrives while this panel is mounted (mirrors a WhatsApp/
  // Telegram read receipt: "seen" means "was actually on screen", not just
  // "downloaded"). Safe to call repeatedly — the backend only ever appends a
  // readBy entry for messages that don't already have one from this user.
  const markSeen = useCallback(() => {
    if (!entityId) return;
    dispatch(markRawContentChatSeen({ authCtx, axiosGlobal, id: entityId, kind }));
  }, [entityId, kind, authCtx, axiosGlobal, dispatch]);

  const load = useCallback(async () => {
    setLoading(true);
    await dispatch(fetchRawContentChat({ authCtx, axiosGlobal, id: entityId, kind, params: { page: 1, limit: 50 } }));
    setLoading(false);
    markSeen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, kind, authCtx, axiosGlobal, dispatch]);

  useEffect(() => { load(); }, [load]);

  // Join/leave the room + listen for real-time deliveries (new message, plus
  // another viewer's edit/delete/seen so every open tab stays in sync).
  useEffect(() => {
    const socket = authCtx.socket;
    if (!socket || !entityId) return;
    const joinEvent  = kind === 'readyToUpload' ? 'dm:joinReadyToUpload'  : 'dm:joinRawContent';
    const leaveEvent = kind === 'readyToUpload' ? 'dm:leaveReadyToUpload' : 'dm:leaveRawContent';
    socket.emit(joinEvent, entityId);
    const matchesThisThread = (msg) => {
      const msgEntityId = kind === 'readyToUpload' ? msg.readyToUploadId : msg.rawContentId;
      return String(msgEntityId) === String(entityId);
    };
    const onNew = (msg) => {
      if (!matchesThisThread(msg)) return;
      dispatch(actions.dmRawChatPush(msg));
      // A message someone ELSE just sent, while this panel is already open —
      // stamp it seen right away rather than waiting for the next mount.
      if (String(msg.senderId) !== myId) markSeen();
    };
    const onEdit = (msg) => { if (matchesThisThread(msg)) dispatch(actions.dmRawChatEdit(msg)); };
    const onDelete = (payload) => { dispatch(actions.dmRawChatSoftDelete(payload)); };
    const onSeen = (payload) => { dispatch(actions.dmRawChatMarkSeen(payload)); };
    socket.on('dm:chat:new', onNew);
    socket.on('dm:chat:edit', onEdit);
    socket.on('dm:chat:delete', onDelete);
    socket.on('dm:chat:seen', onSeen);
    return () => {
      socket.emit(leaveEvent, entityId);
      socket.off('dm:chat:new', onNew);
      socket.off('dm:chat:edit', onEdit);
      socket.off('dm:chat:delete', onDelete);
      socket.off('dm:chat:seen', onSeen);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authCtx.socket, entityId, kind, dispatch, myId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length]);

  const sendMessage = async (payload, withProgress = false) => {
    setSending(true);
    if (withProgress) setSendProgress(0);
    try {
      await dispatch(sendRawContentChatMessage({
        authCtx, axiosGlobal, id: entityId, kind, formData: payload,
        onProgress: withProgress
          ? (e) => setSendProgress(e.total ? Math.round((100 * e.loaded) / e.total) : null)
          : undefined,
      })).unwrap();
      setBody('');
    } catch (_) { /* snackbar already dispatched */ }
    setSending(false);
    setSendProgress(null);
  };

  const handleSendText = () => {
    if (!body.trim()) return;
    const fd = new FormData();
    fd.append('body', body.trim());
    sendMessage(fd);
  };

  const handleAttach = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    sendMessage(fd, true);
  };

  const startRecording = async () => {
    setRecordError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const file = new File([blob], `voice-${Date.now()}.webm`, { type: 'audio/webm' });
        const fd = new FormData();
        fd.append('file', file);
        sendMessage(fd, true);
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setRecording(true);
    } catch (_) {
      setRecordError(t('dm.micAccessDenied'));
    }
  };
  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  // All media opens IN-APP (MediaViewer) — never a browser tab.
  const viewFile = (diskName, name, kindHint) => {
    if (!diskName) return;
    setViewerMedia({
      url: `${axiosGlobal.defaultTargetApi}/uploads/${diskName}`,
      name: name || diskName,
      kind: kindHint || resolveMediaKind(name || diskName),
    });
  };

  // ── Edit / delete — own text messages only (also enforced server-side) ──
  const startEdit = (m) => { setEditingId(m._id); setEditDraft(m.body || ''); };
  const cancelEdit = () => { setEditingId(null); setEditDraft(''); };
  const saveEdit = async (messageId) => {
    const nextBody = editDraft.trim();
    if (!nextBody) return;
    try {
      await dispatch(editRawContentChatMessage({ authCtx, axiosGlobal, id: entityId, kind, messageId, body: nextBody })).unwrap();
      cancelEdit();
    } catch (_) { /* snackbar already dispatched */ }
  };
  const confirmDelete = async () => {
    const messageId = confirmDeleteId;
    setConfirmDeleteId(null);
    try {
      await dispatch(deleteRawContentChatMessage({ authCtx, axiosGlobal, id: entityId, kind, messageId })).unwrap();
    } catch (_) { /* snackbar already dispatched */ }
  };

  // A message's seen-tick looks at the OTHER side of the two-party thread
  // (the creator vs whoever sent this particular message — see
  // rawContentChatModel.js's readBy comment): for a message the CREATOR sent,
  // "seen" means some admin has read it; for a message anyone ELSE sent,
  // "seen" means the creator specifically has read it. ownerId is the
  // record's creator either way (see dmRawContentChatOwnerId).
  const isSeenByCounterpart = (m) => {
    if (!ownerId) return false;
    const senderIsOwner = String(m.senderId) === String(ownerId);
    if (senderIsOwner) {
      return (m.readBy || []).some((r) => String(r.userId) !== String(ownerId));
    }
    return (m.readBy || []).some((r) => String(r.userId) === String(ownerId));
  };

  return (
    <Box sx={{ px: 3, py: 2, borderTop: `1px solid ${T.BD}` }}>
      <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
        color: T.TEXT_TER, mb: 1 }}>
        {total > 0 ? t('dm.chatWithCreatorCount', { count: total }) : t('dm.chatWithCreator')}
      </Typography>

      <Box ref={scrollRef} sx={{ maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={18} sx={{ color: T.TEXT_TER }} />
          </Box>
        ) : messages.length === 0 ? (
          <Typography sx={{ fontSize: '0.76rem', color: T.TEXT_TER, textAlign: 'center', py: 2 }}>
            {t('dm.noMessagesYet')}
          </Typography>
        ) : messages.map((m) => {
          const mine = String(m.senderId) === myId;
          const isEditing = editingId === m._id;
          const canEditDelete = mine && !m.deleted;
          const seen = mine ? isSeenByCounterpart(m) : false;
          return (
            <Box key={m._id} className="dmChatRow" sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.75,
              flexDirection: mine ? 'row-reverse' : 'row',
              '&:hover .dmChatActions': { opacity: canEditDelete && m.type === 'text' && !isEditing ? 1 : 0 } }}>
              {!mine && <UserAvatar userId={m.senderId} size={22} sx={{ mb: 2.25, flexShrink: 0 }} />}

              {/* Edit/delete — own text messages only, revealed on row hover */}
              {canEditDelete && m.type === 'text' && !isEditing && (
                <Box className="dmChatActions" sx={{ display: 'flex', gap: 0.25, opacity: 0, transition: 'opacity 0.15s', mb: 2.25 }}>
                  <Tooltip title={t('common.edit')}>
                    <IconButton size="small" onClick={() => startEdit(m)} sx={{ color: T.TEXT_TER, width: 22, height: 22 }}>
                      <EditOutlinedIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('common.delete')}>
                    <IconButton size="small" onClick={() => setConfirmDeleteId(m._id)} sx={{ color: '#EA005A', width: 22, height: 22 }}>
                      <DeleteOutlineIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start', minWidth: 0 }}>
              {isEditing ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, maxWidth: 260 }}>
                  <TextField size="small" autoFocus value={editDraft} onChange={(e) => setEditDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(m._id); if (e.key === 'Escape') cancelEdit(); }}
                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: T.CTRL_BG, borderRadius: '10px', fontSize: '0.8rem' } }} />
                  <IconButton size="small" onClick={() => saveEdit(m._id)} sx={{ color: '#81c784', width: 24, height: 24 }}>
                    <CheckIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                  <IconButton size="small" onClick={cancelEdit} sx={{ color: T.TEXT_TER, width: 24, height: 24 }}>
                    <CloseIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Box>
              ) : (
              <Box sx={{ maxWidth: '100%', px: 1.25, py: 0.75, borderRadius: '12px',
                bgcolor: mine ? (isDark ? '#ffffff' : '#000000') : T.CTRL_BG,
                color: mine ? (isDark ? '#000000' : '#ffffff') : T.TEXT_PRI }}>
                {!mine && (
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, opacity: 0.7, mb: 0.2 }}>
                    {m.senderName}
                  </Typography>
                )}
                {m.deleted ? (
                  <Typography sx={{ fontSize: '0.78rem', fontStyle: 'italic', opacity: 0.6 }}>
                    {t('dm.messageWasDeleted')}
                  </Typography>
                ) : (
                <>
                {m.type === 'text' && (
                  <Typography sx={{ fontSize: '0.8rem' }}>
                    {m.body}
                    {m.edited && (
                      <Typography component="span" sx={{ fontSize: '0.62rem', opacity: 0.6, ml: 0.5 }}>
                        {t('dm.editedTag')}
                      </Typography>
                    )}
                  </Typography>
                )}
                {m.type === 'voice' && (
                  <audio controls src={`${axiosGlobal.defaultTargetApi}/uploads/${m.fileDiskName}`} style={{ height: 32, maxWidth: 200 }} />
                )}
                {m.type === 'file' && (() => {
                  // Telegram-style inline previews: images and videos render in
                  // the bubble; anything else is a row that opens the viewer.
                  const kind = resolveMediaKind(m.fileName || m.fileDiskName || '');
                  const url  = `${axiosGlobal.defaultTargetApi}/uploads/${m.fileDiskName}`;
                  if (kind === 'image') {
                    return <Box component="img" src={url} alt={m.fileName}
                      onClick={() => viewFile(m.fileDiskName, m.fileName, 'image')}
                      sx={{ maxWidth: 220, maxHeight: 220, borderRadius: '10px', cursor: 'pointer', display: 'block' }} />;
                  }
                  if (kind === 'video') {
                    return (
                      <Box onClick={() => viewFile(m.fileDiskName, m.fileName, 'video')}
                        sx={{ position: 'relative', cursor: 'pointer', maxWidth: 220, borderRadius: '10px', overflow: 'hidden', bgcolor: '#000' }}>
                        <Box component="video" src={`${playbackUrl(url)}#t=0.1`} muted preload="metadata" playsInline
                          sx={{ width: '100%', maxHeight: 180, display: 'block', objectFit: 'cover' }} />
                        <PlayCircleIcon sx={{ position: 'absolute', inset: 0, m: 'auto', fontSize: 34, color: 'rgba(255,255,255,0.9)' }} />
                      </Box>
                    );
                  }
                  if (kind === 'audio') {
                    return <audio controls src={url} style={{ height: 32, maxWidth: 200 }} />;
                  }
                  return (
                    <Typography onClick={() => viewFile(m.fileDiskName, m.fileName, kind)}
                      sx={{ fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
                        '&:hover': { textDecoration: 'underline' } }}>
                      <InsertDriveFileIcon sx={{ fontSize: 14 }} /> {m.fileName}
                    </Typography>
                  );
                })()}
                </>
                )}
              </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mt: 0.25 }}>
                <Typography sx={{ fontSize: '0.62rem', color: T.TEXT_TER }}>{fmtTime(m.date)}</Typography>
                {mine && !m.deleted && (
                  <Tooltip title={seen ? t('dm.seenTip') : t('dm.sentTip')}>
                    {seen
                      ? <DoneAllIcon sx={{ fontSize: 13, color: '#64b5f6' }} />
                      : <CheckIcon sx={{ fontSize: 13, color: T.TEXT_TER }} />}
                  </Tooltip>
                )}
              </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      {can('digitalMarketing:rawContent:chat') && (
        <Box>
          {recordError && <Typography sx={{ fontSize: '0.7rem', color: '#EA005A', mb: 0.5 }}>{recordError}</Typography>}
          {sendProgress !== null && (
            <Box sx={{ mb: 0.75 }}>
              <LinearProgress variant="determinate" value={sendProgress} sx={{ borderRadius: 2, height: 5 }} />
              <Typography sx={{ fontSize: '0.65rem', color: T.TEXT_TER, mt: 0.25 }}>
                {t('dm.uploadingPercent', { percent: sendProgress })}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={recording ? t('dm.stopRecordingTip') : t('dm.recordVoiceMessageTip')}>
              <IconButton size="small" onClick={recording ? stopRecording : startRecording}
                sx={{ color: recording ? '#EA005A' : T.TEXT_TER }}>
                {recording ? <StopCircleIcon sx={{ fontSize: 18 }} /> : <MicIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
            <Tooltip title={t('dm.attachFileTip')}>
              <IconButton size="small" component="label" sx={{ color: T.TEXT_TER }}>
                <AttachFileIcon sx={{ fontSize: 17 }} />
                <input type="file" hidden onChange={handleAttach} />
              </IconButton>
            </Tooltip>
            <TextField size="small" fullWidth placeholder={t('dm.messagePlaceholder')} value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSendText(); }}
              sx={{ '& .MuiOutlinedInput-root': { bgcolor: T.CTRL_BG, borderRadius: '10px', fontSize: '0.8rem' } }} />
            <Button size="small" variant="contained" onClick={handleSendText}
              disabled={sending || !body.trim()}
              sx={{ minWidth: 0, borderRadius: '8px', px: 1.5 }}>
              {sending ? <CircularProgress size={14} color="inherit" /> : <SendIcon sx={{ fontSize: 15 }} />}
            </Button>
          </Box>
        </Box>
      )}

      <MediaViewer open={Boolean(viewerMedia)} onClose={() => setViewerMedia(null)} media={viewerMedia} />

      <ConfirmDialog
        open={Boolean(confirmDeleteId)}
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={confirmDelete}
        title={t('dm.deleteMessageTitle')}
        message={t('dm.deleteMessageMessage')}
        confirmLabel={t('common.delete')}
        destructive
      />
    </Box>
  );
}
